import {
  INTRANET_POLL_OPTION_MAX,
  INTRANET_POLL_OPTION_MIN,
  type IntranetPoll,
} from '@gb-ticket/shared-types';
import { CheckIcon } from 'lucide-react';
import { Link } from 'react-router';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { useVoiceCopy } from '@/platform/voice/use-voice';

export type IntranetPollDraft = { question: string; options: string[] };

export const EMPTY_POLL_DRAFT: IntranetPollDraft = { question: '', options: ['', ''] };

function pollOptionPercent(voteCount: number, totalVotes: number): number {
  if (totalVotes <= 0) {
    return 0;
  }
  return Math.round((voteCount / totalVotes) * 100);
}

/** Unified poll card for feed, article detail, editor and composer preview. */
export function IntranetPollView({
  poll,
  voting,
  pending = false,
  onVote,
  articleHref = null,
  previewMode = false,
}: {
  poll: IntranetPoll;
  voting: boolean;
  pending?: boolean;
  onVote?: (optionId: string) => void;
  /** When set (e.g. feed card), shows „Stimmen ansehen“ after results. Omit on the article itself. */
  articleHref?: string | null;
  /** Composer preview: disabled hint, no voting. */
  previewMode?: boolean;
}) {
  const copy = useVoiceCopy();
  const interactive = voting && !previewMode;
  const awaitingFirstVote = interactive && poll.myOptionId === null;
  const showResults = !interactive || poll.myOptionId !== null || poll.totalVotes > 0;

  return (
    <section
      aria-label="Umfrage"
      className="overflow-hidden rounded-xl border border-border bg-muted/10"
    >
      <div className="flex items-start justify-between gap-3 border-b border-border px-4 py-3">
        <div className="min-w-0 space-y-1">
          <h4 className="text-sm font-semibold text-foreground">{poll.question}</h4>
          <p className="text-xs text-muted-foreground">{copy('intranet.poll.anonymous')}</p>
        </div>
        <p className="shrink-0 text-xs text-muted-foreground">
          {poll.totalVotes === 1 ? '1 Stimme' : `${poll.totalVotes} Stimmen`}
        </p>
      </div>
      <ul className="space-y-2 px-4 py-3">
        {poll.options.map((option) => {
          const percent = pollOptionPercent(option.voteCount, poll.totalVotes);
          const mine = poll.myOptionId === option.id;
          if (awaitingFirstVote) {
            return (
              <li key={option.id}>
                <Button
                  type="button"
                  variant="outline"
                  className="h-auto min-h-11 w-full justify-start px-3 py-2 text-left text-sm font-normal"
                  disabled={pending}
                  onClick={() => onVote?.(option.id)}
                >
                  {option.label}
                </Button>
              </li>
            );
          }
          const resultRow = (
            <>
              <div
                className={cn(
                  'absolute inset-y-0 left-0 transition-[width]',
                  mine ? 'bg-brand-navy/25' : 'bg-muted/60',
                )}
                style={{ width: `${percent}%` }}
                aria-hidden
              />
              <div className="relative flex items-center justify-between gap-3 px-3 py-2.5 text-sm">
                <span className="flex min-w-0 items-center gap-2 font-medium">
                  {mine ? (
                    <CheckIcon className="size-4 shrink-0 text-primary" aria-hidden />
                  ) : (
                    <span className="size-4 shrink-0" aria-hidden />
                  )}
                  <span className="truncate">{option.label}</span>
                </span>
                {showResults ? (
                  <span className="shrink-0 text-muted-foreground tabular-nums">{percent}%</span>
                ) : null}
              </div>
            </>
          );
          return (
            <li key={option.id}>
              {interactive ? (
                <button
                  type="button"
                  disabled={pending || mine}
                  aria-pressed={mine}
                  aria-label={
                    mine
                      ? copy('intranet.poll.your-vote', { label: option.label })
                      : `Stimme ändern: ${option.label}`
                  }
                  onClick={() => onVote?.(option.id)}
                  className={cn(
                    'relative w-full overflow-hidden rounded-lg border border-border bg-background text-left transition-colors',
                    'focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
                    mine
                      ? 'border-brand-navy/40'
                      : 'hover:border-brand-navy/30 hover:bg-muted/30 disabled:opacity-100',
                  )}
                >
                  {resultRow}
                </button>
              ) : (
                <div
                  className={cn(
                    'relative overflow-hidden rounded-lg border border-border bg-background',
                    mine && 'border-brand-navy/40',
                  )}
                >
                  {resultRow}
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {interactive && poll.myOptionId !== null ? (
        <p className="px-4 pb-3 text-xs text-muted-foreground">
          {copy('intranet.poll.change-vote')}
        </p>
      ) : null}
      {articleHref && showResults ? (
        <div className="border-t border-border px-4 py-3">
          <Button variant="secondary" className="w-full" asChild>
            <Link to={articleHref}>Stimmen ansehen</Link>
          </Button>
        </div>
      ) : null}
      {previewMode ? (
        <p className="border-t border-border px-4 py-2 text-xs text-muted-foreground">
          Abstimmen ist in der Vorschau aus.
        </p>
      ) : null}
      {poll.locked && !voting && !previewMode ? (
        <p className="border-t border-border px-4 py-2 text-xs text-muted-foreground">
          Die Antworten können nicht mehr geändert werden, weil schon abgestimmt wurde.
        </p>
      ) : null}
    </section>
  );
}

export function pollFromDraft(draft: IntranetPollDraft): IntranetPoll | null {
  const question = draft.question.trim();
  const labels = draft.options.map((option) => option.trim());
  if (!question && labels.every((label) => !label)) {
    return null;
  }
  return {
    id: 'preview',
    question: question || 'Frage',
    options: labels.map((label, index) => ({
      id: `preview-${index}`,
      label: label || `Antwort ${index + 1}`,
      voteCount: 0,
    })),
    totalVotes: 0,
    myOptionId: null,
    locked: false,
  };
}

export function IntranetPollDraftFields({
  draft,
  onChange,
}: {
  draft: IntranetPollDraft;
  onChange: (draft: IntranetPollDraft) => void;
}) {
  function setOption(index: number, label: string) {
    onChange({
      ...draft,
      options: draft.options.map((option, current) => (current === index ? label : option)),
    });
  }

  return (
    <fieldset className="space-y-3 rounded-xl border border-border bg-card p-4 text-card-foreground shadow-sm">
      <legend className="px-1 text-sm font-medium">Umfrage</legend>
      <div className="space-y-2">
        <Label htmlFor="intranet-poll-question">Frage</Label>
        <Input
          id="intranet-poll-question"
          value={draft.question}
          maxLength={200}
          onChange={(event) => onChange({ ...draft, question: event.target.value })}
        />
      </div>
      <div className="space-y-2">
        {draft.options.map((option, index) => (
          <div key={index} className="space-y-2">
            <Label htmlFor={`intranet-poll-option-${index}`}>Antwort {index + 1}</Label>
            <Input
              id={`intranet-poll-option-${index}`}
              value={option}
              maxLength={80}
              onChange={(event) => setOption(index, event.target.value)}
            />
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        {draft.options.length < INTRANET_POLL_OPTION_MAX ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onChange({ ...draft, options: [...draft.options, ''] })}
          >
            Antwort hinzufügen
          </Button>
        ) : null}
        {draft.options.length > INTRANET_POLL_OPTION_MIN ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onChange({ ...draft, options: draft.options.slice(0, -1) })}
          >
            Letzte Antwort entfernen
          </Button>
        ) : null}
      </div>
      <p className="text-xs text-muted-foreground">Abstimmen ist hier aus.</p>
    </fieldset>
  );
}
